import {
  BowlingFrame,
  BowlingParticipant,
  BowlingState,
  createEmptyFrame
} from '../interfaces/bowling.interface';

/**
 * Pure scoring helpers for the bowling mini-game. **No side-effects
 * beyond the objects passed in** — these functions mutate the frame
 * and participant data they're given but never reach for `state` or
 * any service. Easy to unit-test (see `bowling-score.spec.ts`).
 *
 * Scoring model is intentionally simplified: every bowler gets exactly
 * one roll per frame, no second roll, no strike/spare bonuses. Total
 * is just the running sum of pins knocked across 10 frames. Maximum
 * achievable score = 100.
 */

/** Total frames per side in one game. */
const TOTAL_FRAMES = 10;
/** Pin cap per roll — anything higher is clamped on input. */
const MAX_PINS = 10;

/**
 * Build a 10-element array of empty frames — one per side at game
 * start. Each frame has `roll1: null` so the scorecard renders an
 * empty cell until the first roll lands.
 */
export function buildEmptyFrames(): BowlingFrame[] {
  return Array.from({ length: TOTAL_FRAMES }, () => createEmptyFrame());
}

/**
 * Write the bowler's one roll into the given frame. Clamps to the
 * legal `0 ≤ pins ≤ 10` range and floors any decimal so a fractional
 * pin count from the physics layer (rare, defensive) becomes an int.
 *
 * Caller must follow with `recomputeCumulative` to refresh the running
 * totals — this function intentionally only writes `roll1`, not the
 * cumulative line.
 */
export function applyRoll(frame: BowlingFrame, pins: number): void {
  frame.roll1 = Math.max(0, Math.min(MAX_PINS, Math.floor(pins)));
}

/**
 * Walk all 10 frames in order, summing `roll1` values into each
 * frame's `cumulativeScore`. Stops at the first un-bowled frame
 * (`roll1 === null`) and clears every cumulative from that point
 * forward, so the scorecard never shows a stale running total for an
 * unplayed frame.
 */
export function recomputeCumulative(frames: BowlingFrame[]): void {
  let running = 0;
  for (let i = 0; i < frames.length; i++) {
    const frame = frames[i];
    if (frame.roll1 === null) {
      // Unplayed frame — clear this cumulative and everything after.
      frame.cumulativeScore = null;
      for (let j = i + 1; j < frames.length; j++) frames[j].cumulativeScore = null;
      return;
    }
    running += frame.roll1;
    frame.cumulativeScore = running;
  }
}

/**
 * After a roll has been applied, advance the turn pointer. Within a
 * frame, side 0 (A) goes first then side 1 (B). When B finishes its
 * roll the frame is complete for both sides, so:
 *
 *   - Each side rotates its student cursor to the next bowler in the
 *     roster (so team mode cycles through every student).
 *   - `currentSide` resets to 0.
 *   - `currentFrame` increments.
 *
 * Once `currentFrame` reaches `TOTAL_FRAMES` it stays there as a
 * sentinel meaning "game over" — the caller (`MiniGameService`) flips
 * `state.phase` to `FINISHED` and routes to the summary screen.
 */
export function advanceBowlingTurn(state: BowlingState): void {
  if (state.currentSide === 0) {
    // A just rolled — B is next, frame stays the same.
    state.currentSide = 1;
    return;
  }

  // Side B just rolled — frame is complete for both sides. Rotate
  // each side's student cursor so the next round starts with the
  // next bowler in the roster.
  state.participants.forEach(p => {
    if (p.students.length > 0) {
      p.currentStudentIdx = (p.currentStudentIdx + 1) % p.students.length;
    }
  });
  state.currentSide = 0;
  state.currentFrame += 1;

  if (state.currentFrame >= TOTAL_FRAMES) {
    // Sentinel — game-over; clamp so further `+= 1` calls don't drift.
    state.currentFrame = TOTAL_FRAMES;
  }
}

/**
 * Compare both sides' totals and return who won. `'tie'` if equal —
 * the summary screen handles the tie case with a special label.
 * Caller writes the result to `BowlingState.winnerSide`.
 */
export function determineWinner(
  participants: [BowlingParticipant, BowlingParticipant]
): 0 | 1 | 'tie' {
  const a = participants[0].totalScore;
  const b = participants[1].totalScore;
  if (a > b) return 0;
  if (b > a) return 1;
  return 'tie';
}

/**
 * One-shot helper called after each roll by `MiniGameService`:
 * recomputes the cumulative line and copies the last-known cumulative
 * into the participant's cached `totalScore` (so views can read a
 * single number without scanning the frames array every CD pass).
 */
export function refreshTotals(participant: BowlingParticipant): void {
  recomputeCumulative(participant.frames);
  participant.totalScore = lastKnownCumulative(participant.frames);
}

/**
 * Walk frames in reverse and return the most recent non-null
 * cumulative — i.e. the running total through the last frame the
 * bowler has actually played. Returns 0 if nothing's been bowled yet.
 */
function lastKnownCumulative(frames: BowlingFrame[]): number {
  for (let i = frames.length - 1; i >= 0; i--) {
    if (frames[i].cumulativeScore !== null) return frames[i].cumulativeScore!;
  }
  return 0;
}
